import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Info, Zap, ShieldCheck, Star, Search, Shield, Server, Users, DollarSign, TrendingUp, BarChart3, PieChart, LineChart } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Acompanhar o Crescimento da Sua Revenda IPTV", href: "/blog/como-acompanhar-crescimento-revenda-iptv" }
  ];

  const sections = [
    {
      title: "Métricas: O Termômetro da Sua Revenda",
      content: (
        <div className="prose prose-invert max-w-none space-y-6 text-lg text-muted-foreground">
          <p>
            No mundo dos negócios digitais em 2026, quem não mede, não gerencia. Acompanhar o crescimento da sua revenda de IPTV vai muito além de olhar o saldo no final do mês. É preciso entender o <strong>porquê</strong> dos números estarem subindo ou descendo para garantir a sustentabilidade do seu império.
          </p>
          <p>
            Muitos revendedores se iludem com faturamentos brutos altos, mas ignoram custos ocultos, taxa de perda de clientes (churn) e o custo real para adquirir cada novo usuário. O acompanhamento profissional transforma sua revenda em uma empresa previsível e lucrativa.
          </p>
          <div className="bg-electric/10 border-l-4 border-electric p-8 my-8 rounded-r-3xl">
            <h4 className="text-white font-black mb-2 uppercase tracking-tighter italic text-xl">Visão de Master</h4>
            <p className="italic text-white/90">
              "Faturamento é vaidade, lucro é sanidade, caixa é realidade. Se você não sabe seus números, você não tem um negócio, você tem um hobby caro."
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Os KPIs Essenciais para Revendedores IPTV",
      type: "grid" as const,
      content: (
        <>
          {[
            { t: "MRR (Receita Recorrente)", d: "O valor total que você recebe mensalmente de assinaturas. É o indicador mais importante de estabilidade financeira.", i: <DollarSign /> },
            { t: "Churn Rate (Cancelamento)", d: "A porcentagem de clientes que abandonam seu serviço a cada mês. Sua meta deve ser manter este número abaixo de 5%.", i: <TrendingUp /> },
            { t: "LTV (Valor do Tempo de Vida)", d: "Quanto um cliente médio gasta com você antes de cancelar. Isso define quanto você pode investir em marketing.", i: <BarChart3 /> },
            { t: "CAC (Custo de Aquisição)", d: "Quanto você gasta em anúncios ou tempo para conseguir um novo cliente. Deve ser sempre menor que o LTV.", i: <PieChart /> },
            { t: "Taxa de Renovação", d: "Porcentagem de clientes que pagam a fatura do mês seguinte sem atrasos. Indica a satisfação com o serviço.", i: <LineChart /> },
            { t: "Crescimento Líquido", d: "Número de novos clientes menos o número de cancelados. É o indicador real de expansão da sua base.", i: <Zap /> }
          ].map((item, i) => (
            <div key={i} className="p-8 rounded-[2.5rem] bg-white/[0.02] border border-white/5 hover:border-electric/30 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-electric/10 text-electric flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {item.i}
              </div>
              <h3 className="text-2xl font-black tracking-tighter mb-4 italic uppercase">{item.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{item.d}</p>
            </div>
          ))}
        </>
      )
    },
    {
      title: "Utilizando o Painel P2 PRO para Analisar Dados",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-lg text-muted-foreground">
            O <strong>Painel P2 PRO</strong> não é apenas uma ferramenta de ativação, é um hub de inteligência de dados. Através dos relatórios nativos, você consegue visualizar a curva de crescimento da sua operação sem precisar de planilhas complexas.
          </p>
          <div className="p-10 rounded-[3rem] bg-white/[0.02] border border-white/5 my-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-electric/5 blur-[80px] rounded-full" />
            <h4 className="text-2xl font-black mb-6 italic uppercase tracking-tighter flex items-center gap-3">
              <Search className="text-electric w-8 h-8" /> Checklist do Crescimento Mensal
            </h4>
            <ul className="space-y-4">
              <li className="flex gap-4 items-start">
                <Check className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Análise de Origem:</span> Identifique de onde vêm seus melhores clientes (Indicação, Facebook, Instagram, Google).</p>
              </li>
              <li className="flex gap-4 items-start">
                <Check className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Monitoramento de Inatividade:</span> Clientes que não logam há muito tempo são potenciais cancelamentos. Reative-os com ofertas especiais.</p>
              </li>
              <li className="flex gap-4 items-start">
                <Check className="text-electric shrink-0 mt-1" />
                <p><span className="text-white font-bold">Previsão de Fluxo:</span> Calcule quanto você terá de pagar no acerto do pós-pago e quanto sobrará de lucro limpo no seu bolso.</p>
              </li>
            </ul>
          </div>
          <p className="text-lg text-muted-foreground">
            Crescer de forma desordenada é perigoso. Com o modelo pós-pago da P2 PRO, seu risco financeiro é reduzido, permitindo que você foque totalmente em analisar estas métricas e escalar sua operação com segurança e inteligência.
          </p>
        </div>
      )
    }
  ];

  return (
    <SEOPage
      category="Dicas Painel IPTV POS PAGO"
      title="Como Acompanhar o Crescimento da Sua Revenda IPTV: Métricas de Sucesso"
      subtitle="Pare de adivinhar seus lucros. Aprenda a analisar os indicadores que realmente importam para escalar sua revenda com base em dados reais."
      heroImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
      introParagraphs={[
        "Muitos revendedores master começam a crescer e se perdem no próprio sucesso. O dinheiro entra, mas eles não sabem quanto é lucro real e quanto é custo operacional. Ter uma visão clara das métricas do seu negócio é o que diferencia os grandes players dos amadores.",
        "Neste artigo, vamos dissecar os principais KPIs (Indicadores de Performance) para o mercado de IPTV em 2026. Vamos falar sobre Churn, LTV, CAC e como utilizar os relatórios do Painel P2 PRO para tomar decisões estratégicas.",
        "Se você quer que sua revenda dure anos e se torne sua principal fonte de renda, você precisa aprender a ler os sinais que o seu negócio está te dando. Vamos transformar números frios em estratégias de lucro."
      ]}
      benefits={[
        { title: "Decisões Assertivas", description: "Saiba exatamente onde investir seu tempo e dinheiro para crescer mais rápido." },
        { title: "Saúde Financeira", description: "Tenha clareza total sobre suas margens de lucro e fluxo de caixa mensal." },
        { title: "Previsibilidade", description: "Consiga prever quanto seu negócio estará faturando daqui a 6 meses." },
        { title: "Redução de Riscos", description: "Identifique problemas de cancelamento antes que eles comprometam seu faturamento." }
      ]}
      howItWorks={[
        { title: "Coleta de Dados", description: "Acesse os relatórios mensais do seu painel administrativo." },
        { title: "Cálculo de KPIs", description: "Aplique as fórmulas de MRR, Churn e LTV na sua base." },
        { title: "Identificação de Gargalos", description: "Descubra onde você está perdendo dinheiro ou clientes." },
        { title: "Ajuste de Rota", description: "Mude sua estratégia de atendimento ou marketing baseada nos dados." }
      ]}
      diferenciais={[
        { title: "Relatórios Exportáveis", description: "Dados prontos para análise em Excel ou ferramentas de BI." },
        { title: "Histórico Completo", description: "Acompanhe a evolução da sua rede desde o primeiro dia de ativação." },
        { title: "Dashboard Intuitivo", description: "Gráficos fáceis de entender que resumem a saúde do seu negócio." }
      ]}
      faq={[
        { question: "Qual é uma taxa de churn aceitável?", answer: "No mercado de IPTV, manter o churn abaixo de 5% é excelente. Acima de 10%, você deve investigar imediatamente a qualidade do seu suporte ou sinal." },
        { question: "Como calcular o LTV do meu cliente?", answer: "Multiplique o valor da sua mensalidade pelo número médio de meses que o cliente permanece ativo. Ex: R$ 35,00 x 12 meses = R$ 420,00 de LTV." },
        { question: "O painel pós-pago facilita essa análise?", answer: "Sim, pois como você não precisa se preocupar com estoque de créditos, seu foco fica totalmente na gestão da base e na rentabilidade por linha ativa." }
      ]}
      breadcrumbs={breadcrumbs}
      sections={sections}
      ctaTitle="🚀 Quer Ver Seu Negócio Crescer de Verdade?"
      ctaDescription="Junte-se à P2 PRO e utilize a inteligência de dados do nosso painel para dominar o mercado de IPTV."
      ctaButtonText="Quero meu Painel Master"
    />
  );
};

export const Route = createFileRoute("/blog/como-acompanhar-crescimento-revenda-iptv")({
  head: () => ({
    meta: [
      { title: "Como Acompanhar o Crescimento da Sua Revenda IPTV | P2 PRO" },
      { name: "description", content: "Aprenda a analisar as métricas de crescimento da sua revenda IPTV. Guia completo sobre MRR, Churn, LTV e gestão baseada em dados reais." },
      { name: "keywords", content: "crescimento revenda iptv, metricas iptv, churn rate iptv, lucro revenda iptv" }
    ],
  }),
  component: ArticlePage,
});
